import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  ConnectedPosition,
  STANDARD_DROPDOWN_ADJACENT_POSITIONS,
  STANDARD_DROPDOWN_BELOW_POSITIONS,
} from '@angular/cdk/overlay';

export interface MultiSelectDropdownItem {
  id: string;
  label: string;
}

@Component({
  selector: 'multi-select-dropdown',
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.scss'],
  standalone: false,
})
export class MultiSelectDropdownComponent {
  private static nextPanelId = 0;

  @Input() items: MultiSelectDropdownItem[] = [];
  @Input() selectedIds: readonly string[] = [];
  @Input() open = false;
  @Input() triggerText = 'Add Calendar';
  /** Element id for the trigger button (e2e, aria-labelledby). */
  @Input() triggerId = '';
  /** Space-separated element ids for aria-labelledby. */
  @Input() ariaLabelledBy = '';
  @Output() openChange = new EventEmitter<boolean>();
  @Output() selectionToggle = new EventEmitter<string>();

  @ViewChild('triggerButton')
  private triggerButtonRef?: ElementRef<HTMLButtonElement>;

  private readonly generatedPanelId = `multi-select-dropdown-panel-${MultiSelectDropdownComponent.nextPanelId++}`;

  /**
   * Below the trigger, then above it, then beside it. The overlay picks the
   * first position that fits, so a short window flips the panel instead of
   * clipping it.
   */
  readonly panelPositions: ConnectedPosition[] = [
    ...STANDARD_DROPDOWN_BELOW_POSITIONS.map((position) => ({
      ...position,
      offsetY: position.overlayY === 'top' ? 4 : -4,
    })),
    ...STANDARD_DROPDOWN_ADJACENT_POSITIONS,
  ];

  get panelId(): string {
    return this.triggerId ? `${this.triggerId}-panel` : this.generatedPanelId;
  }

  onTriggerClick(ev: Event): void {
    ev.stopPropagation();
    this.openChange.emit(!this.open);
  }

  onOutsideClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    // The click still has to reach the control underneath, so leave the event
    // alone and do not move focus back to the trigger.
    void event;
    this.openChange.emit(false);
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePanel();
    }
  }

  onOverlayAttach(): void {
    // Move focus into the overlay so Tab reaches the option rows instead of
    // the page behind the backdrop. Prefer the first checkbox; fall back to
    // the listbox panel itself.
    setTimeout(() => {
      const panel = document.getElementById(this.panelId);
      if (!panel) {
        return;
      }
      const firstCheckbox = panel.querySelector(
        'input[type="checkbox"]',
      ) as HTMLElement | null;
      (firstCheckbox ?? panel).focus();
    });
  }

  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  onToggleItem(id: string): void {
    this.selectionToggle.emit(id);
  }

  private closePanel(): void {
    if (!this.open) {
      return;
    }
    this.openChange.emit(false);
    this.triggerButtonRef?.nativeElement.focus();
  }
}
